/**
 * Utility functions for checking authentication status
 */

// Cache to ensure auth check happens only once
let authCheckPromise: Promise<boolean> | null = null;

/**
 * Check if user is currently authenticated
 * Returns true if user has valid tokens in storage
 * Ensures only one network call is made at a time
 */
export const isAuthenticated = async (): Promise<boolean> => {
	if (typeof window === "undefined") return false;

	// If there's already an auth check in progress, return that promise
	if (authCheckPromise) {
		return authCheckPromise;
	}

	// Create a new auth check promise
	authCheckPromise = (async () => {
		try {
			const { getCurrentSession } = await import("@/services/cognito");
			await getCurrentSession();
			return true;
		} catch {
			// If getCurrentSession throws, user is not authenticated
			return false;
		} finally {
			// Clear the promise after a short delay to allow reuse
			setTimeout(() => {
				authCheckPromise = null;
			}, 100);
		}
	})();

	return authCheckPromise;
};

/**
 * Check if user is authenticated synchronously (checks localStorage for tokens)
 * This is a faster check but less reliable than isAuthenticated()
 */
export const isAuthenticatedSync = (): boolean => {
	if (typeof window === "undefined") return false;

	try {
		// AWS Amplify stores tokens in localStorage with keys like:
		// - CognitoIdentityServiceProvider.{clientId}.{username}.accessToken
		// - CognitoIdentityServiceProvider.{clientId}.LastAuthUser
		// We can check for the LastAuthUser key as a quick check
		const keys = Object.keys(localStorage);
		const hasCognitoKeys = keys.some((key) =>
			key.includes("CognitoIdentityServiceProvider")
		);
		return hasCognitoKeys;
	} catch {
		return false;
	}
};
